import { request } from '@mairistem/tools';
import type {} from '@redux-devtools/extension';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getCyclesAccueil } from '@/routes/cycle';
import type { ApiResponseType, CacheMetadataType, CycleAccueilType, CycleType } from '@/types';
import { CACHE_TTL, isCacheValid } from '@/utils/cache';
import { calculerDebutPeriode, formatCycles } from '@/utils/cycle';

type CyclesStoreType = {
  data: CycleAccueilType[];
  loading: boolean;
  /** Requête en cours, y compris lors d'un rafraîchissement silencieux */
  fetching: boolean;
  error: string | null;
  cache: CacheMetadataType & { periodicite: string | null };

  fetch: (
    regieId: number,
    periodicite: string,
    regieProlongee: boolean,
    joursEncaissementSupplementaires: number,
    force?: boolean,
  ) => Promise<void>;
  clear: () => void;
};

const useCyclesStore = create<CyclesStoreType>()(
  devtools(
    (set, get) => ({
      data: [],
      loading: false,
      fetching: false,
      error: null,
      cache: {
        timestamp: null,
        regieId: null,
        periodicite: null,
      },

      fetch: async (
        regieId: number,
        periodicite: string,
        regieProlongee: boolean,
        joursEncaissementSupplementaires: number,
        force = false,
      ) => {
        // Guard : éviter fetch concurrent
        if (get().fetching) return;

        const currentCache = get().cache;
        // Skip si cache valide et pas de force
        if (
          !force &&
          currentCache.periodicite === periodicite &&
          isCacheValid(currentCache.timestamp, CACHE_TTL.CYCLES, currentCache.regieId, regieId)
        ) {
          return;
        }

        // Ne montrer le loading que si on n'a pas encore de données
        // Pour un refresh invisible à l'utilisateur
        const hasData = get().data.length > 0;
        set({ loading: !hasData, fetching: true, error: null });

        try {
          const dateLimiteSince = calculerDebutPeriode(periodicite);
          const cyclesUrl = getCyclesAccueil({
            page: 1,
            perPage: 100,
            sorts: [],
            filters: [],
            dateLimiteSince,
          });
          const response: ApiResponseType<CycleType> = await request.get(cyclesUrl);

          const formattedCycles = formatCycles(
            response.data,
            regieProlongee,
            joursEncaissementSupplementaires,
          );

          set({
            data: formattedCycles,
            loading: false,
            fetching: false,
            cache: {
              timestamp: Date.now(),
              regieId,
              periodicite,
            },
          });
        } catch (error) {
          set({
            loading: false,
            fetching: false,
            error: (error as Error).message || 'Erreur lors du chargement des cycles',
          });
        }
      },

      clear: () => {
        set({
          data: [],
          loading: false,
          fetching: false,
          error: null,
          cache: { timestamp: null, regieId: null, periodicite: null },
        });
      },
    }),
    { name: 'CyclesStore' },
  ),
);

export default useCyclesStore;
